import { compareByCodePoint, type Column, type Project, type TicketSummary } from '@vd/types';

/**
 * Where a report sent through the widget lands (TOADO-975), by the server's
 * own rule (`resolveIntakeColumnId` in apps/api/src/widgets/store.ts): the
 * column marked Bug intake when the project has Bug intake on, otherwise the
 * first work column in board order (order, then id by code point), and the
 * first column of any kind on a board that has only the intake one. Change
 * the two together.
 */
export type ReportDestination =
  | { kind: 'intake'; column: Column }
  | { kind: 'first'; column: Column; intakeWithoutColumn: boolean }
  | { kind: 'none' };

export function reportDestination(project: Pick<Project, 'bugIntakeEnabled'>, columns: readonly Column[]): ReportDestination {
  const intake = columns.find((column) => column.isIntake);
  if (project.bugIntakeEnabled && intake) return { kind: 'intake', column: intake };
  const ranked = [...columns].sort((a, b) => (a.isIntake === b.isIntake ? 0 : a.isIntake ? 1 : -1) || a.order - b.order || compareByCodePoint(a.id, b.id));
  const first = ranked[0];
  return first ? { kind: 'first', column: first, intakeWithoutColumn: !!project.bugIntakeEnabled } : { kind: 'none' };
}

/**
 * A time the API sent, read as UTC when it names no zone (Postgres's timestamp
 * text), as the API's own databaseTime does. Fractions past milliseconds are cut
 * first (review round 1): Date.parse reads six digits on some engines only.
 */
export function serverTime(value: string): number {
  const iso = /(?:[zZ]|[+-]\d\d(?::?\d\d)?)$/.test(value) ? value : `${value.replace(' ', 'T')}Z`;
  return Date.parse(iso.replace(/(\.\d{3})\d+/, '$1'));
}

/** Every way a report reaches a project through the widget. */
// team_panel too (review round 3): a teammate the widget remembers files from its team panel.
const WIDGET_SOURCES = new Set(['widget', 'public_link', 'team_snippet', 'team_panel']);

/** A report that came in through the widget and has landed: a draft (a handoff not yet saved) has not (review round 2). */
const isWidgetReport = (ticket: TicketSummary) => ticket.source !== undefined && WIDGET_SOURCES.has(ticket.source) && !ticket.isDraft;

/** The widget reports on a board now, to tell a new one from them later. */
export function widgetReportIds(tickets: readonly TicketSummary[]): ReadonlySet<string> {
  return new Set(tickets.filter(isWidgetReport).map((ticket) => ticket.id));
}

/**
 * The newest widget report that is not among `known` (review round 1: by what
 * was already there, not by a time, so neither clock decides), if any.
 */
export function newWidgetReport(tickets: readonly TicketSummary[], known: ReadonlySet<string>): TicketSummary | null {
  const reports = tickets
    .filter((ticket) => isWidgetReport(ticket) && !known.has(ticket.id))
    .map((ticket) => ({ ticket, at: serverTime(ticket.createdAt) }));
  reports.sort((x, y) => y.at - x.at);
  return reports[0]?.ticket ?? null;
}
